import { motion } from 'framer-motion';
import { History, Users, MapPin, Award, Building2, FileText, Truck } from 'lucide-react';
import Section from '../components/ui/Section';
import SEO from '../components/ui/SEO';
import { companyInfo } from '../data';

export default function About() {
  const values = [
    {
      title: "20 års erfaring",
      description: "Det er nu 20 år siden, vi startede op. Vores lange erfaring sikrer dig den bedste rådgivning og udførelse.",
      icon: History
    },
    {
      title: "Lokalt dækkende",
      description: "Vi er forankret lokalt og kører primært i vores eget nærområde, hvor vi kender vejene ud og ind.",
      icon: MapPin
    },
    {
      title: "Kvalitet & Pålidelighed",
      description: "Vi går aldrig på kompromis med kvaliteten af vores arbejde eller sikkerheden af dit gods.",
      icon: Award
    },
    {
      title: "Personlig service",
      description: "Vi er et lille, tæt team på 3 ansatte, hvilket sikrer dig en personlig og nærværende dialog.",
      icon: Users
    }
  ];

  return (
    <>
      <SEO 
        title="Om os" 
        description="Læs historien om NR. GALTEN VOGNMANDSFORRETNING ApS. Vi startede for 20 år siden, er 3 ansatte og kører i lokalområdet med vores to biler." 
      />

      {/* Hero */}
      <section className="pt-40 pb-20 bg-slate-50 text-slate-900 relative overflow-hidden">
        {/* Skewed Background Element */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[#1d398e] hidden lg:block skew-x-[-12deg] translate-x-20 z-0" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center space-x-2 bg-blue-50 text-[#1d398e] px-4 py-2 rounded-full text-sm font-bold mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1d398e] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1d398e]"></span>
              </span>
              <span className="tracking-wide uppercase">Vores historie</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-[1.1] tracking-wide text-slate-900">
              Om <span className="text-[#1d398e]">NR. GALTEN</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed max-w-2xl">
              En lokal vognmandsforretning med 20 års erfaring, bygget på ordentlighed, punktlighed og god service.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img 
              src="https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/39611fe8-c0b6-43e2-a76d-d4bb5f9c732d.jpg" 
              alt="Vores biler - NR. GALTEN VOGNMANDSFORRETNING ApS" 
              className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
            />
            <div className="absolute -top-6 -right-6 bg-blue-900 text-white p-8 rounded-2xl shadow-xl hidden md:block">
              <p className="text-4xl font-bold mb-1">20 År</p>
              <p className="text-sm opacity-80">Erfaring i branchen</p>
            </div>
          </div>
          <div>
            <h2 className="text-xl md:text-3xl font-bold text-blue-900 mb-6">20 år med lokal vognmandskørsel</h2>
            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p>
                Det er nu 20 år siden, at NR. GALTEN VOGNMANDSFORRETNING ApS startede op. Gennem to årtier har vi leveret pålidelig og professionel vognmandskørsel til både private og erhvervskunder.
              </p>
              <p>
                Vi er i dag et stærkt og stabilt team på <strong>3 ansatte</strong>, som sætter en stor ære i at yde den bedste service. Vi kører primært i <strong>lokalområdet</strong>, hvor vi har opbygget et solidt kendskab til vejene og vores kunders behov.
              </p>
              <p>
                Til at løse vores daglige opgaver har vi <strong>to biler</strong>, som er fuldt udstyret til at håndtere alt fra transport med salg og levering af materialer til jordflytning og containerudlejning. Vores mindre størrelse er vores styrke – det gør os fleksible, hurtige og sikrer, at du altid taler direkte med den, der løser opgaven.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Values Grid */}
      <Section light>
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-xl md:text-4xl font-bold text-blue-900 mb-4">Vores Værdier</h2>
          <p className="text-gray-600">
            Disse værdier er kernen i alt, hvad vi gør, og de sikrer, at du altid får en professionel oplevelse hos os.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => (
            <div key={index} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center">
              <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center text-blue-900 mb-6 mx-auto">
                <value.icon className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-3">{value.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {value.description}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* Info Cards */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-12 lg:grid-cols-3 gap-6">
          {[
            { label: "Virksomhedsnavn", value: companyInfo.name, icon: Building2 },
            { label: "CVR Nummer", value: companyInfo.cvr, icon: FileText },
            { label: "Hovedsæde", value: companyInfo.address, icon: MapPin }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`group bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-xl hover:border-blue-100 transition-all duration-500 ${
                index === 2 ? 'md:col-span-12 lg:col-span-1' : 'md:col-span-6 lg:col-span-1'
              }`}
            >
              <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-[#1d398e] mb-6 group-hover:scale-110 transition-transform duration-500">
                <item.icon className="w-6 h-6" />
              </div>
              <h4 className="text-xs font-bold text-[#1d398e]/60 uppercase tracking-[0.2em] mb-3">{item.label}</h4>
              <p className="text-lg font-black text-slate-900 leading-tight">{item.value}</p>
            </motion.div>
          ))}
        </div>
      </Section>
    </>
  );
}
